import type { NamedSet } from 'zustand/middleware';
import type { StateCreator, StoreMutatorIdentifier } from 'zustand/vanilla';

import type { BeforeFiltersChange } from './SearchStoreProvider';

type BeforeStateChangeCallback<T> = (proposedState: T) => ReturnType<BeforeFiltersChange>;
type DevtoolsMutator = ['zustand/devtools', never];
type DevtoolsAction = Parameters<NamedSet<object>>[2];

type BeforeStateChange = <T, Mcs extends [StoreMutatorIdentifier, unknown][] = []>(
  f: StateCreator<T, [DevtoolsMutator], Mcs>,
  callback?: BeforeStateChangeCallback<T>,
) => StateCreator<T, [DevtoolsMutator], Mcs>;

const shouldCallBeforeStateChange = (action: DevtoolsAction): boolean =>
  !(typeof action === 'object' && action.shouldCallBeforeFiltersChangeCallback === false);

const isStateUpdater = <T, R>(partial: T | R | ((state: T) => R)): partial is ((state: T) => R) =>
  typeof partial === 'function';

export const beforeStateChange: BeforeStateChange = (f, onBeforeStateChange) => (set, get, store) => {
  type State = ReturnType<typeof get>;
  type SetArgs = typeof set extends {
    (...args: infer MergeArgs): void;
    (...args: infer ReplaceArgs): void;
  }
    ? MergeArgs | ReplaceArgs
    : never;

  const beforeSet: typeof set = (...args: SetArgs) => {
    const currentState = get();
    if (args[1] === true) {
      const [partial, , action] = args;
      const nextState = isStateUpdater<State, State>(partial) ? partial(currentState) : partial;

      if (shouldCallBeforeStateChange(action) && onBeforeStateChange?.(nextState)?.redirectDidHappen) {
        return;
      }

      set(nextState, true, action);
    } else {
      const [partial, , action] = args;
      const nextState = isStateUpdater<State, State | Partial<State>>(partial) ? partial(currentState) : partial;

      const proposedState = Object.assign({}, currentState, nextState);

      if (shouldCallBeforeStateChange(action) && onBeforeStateChange?.(proposedState)?.redirectDidHappen) {
        return;
      }

      set(nextState, false, action);
    }
  };

  return f(beforeSet, get, store);
};
